import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  workbenchTemplatesApi,
  type SaveTemplateRequest,
  type UpdateTemplateRequest,
  type WorkbenchTemplate,
} from '@/api/workbenchTemplates';
import { LAYOUT_TEMPLATE_SURFACE } from './layoutTemplates';

const QUERY_KEY = ['workbench', 'templates'] as const;

function keyFor(surface?: string) {
  return surface ? [...QUERY_KEY, surface] : QUERY_KEY;
}

function upsertTemplate(
  current: WorkbenchTemplate[] | undefined,
  saved: WorkbenchTemplate,
): WorkbenchTemplate[] {
  const existing = current ?? [];
  const withoutSaved = existing.filter((template) => template.template_id !== saved.template_id);
  return [saved, ...withoutSaved];
}

export function useTemplatesList(
  surface?: string,
  opts: { enabled?: boolean } = {},
): UseQueryResult<WorkbenchTemplate[]> {
  return useQuery({
    queryKey: keyFor(surface),
    queryFn: () => workbenchTemplatesApi.list(surface),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useLayoutTemplates(
  opts: { enabled?: boolean } = {},
): UseQueryResult<WorkbenchTemplate[]> {
  return useTemplatesList(LAYOUT_TEMPLATE_SURFACE, opts);
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: SaveTemplateRequest) => workbenchTemplatesApi.save(req),
    onSuccess: (saved) => {
      qc.setQueryData<WorkbenchTemplate[]>(keyFor(), (current) => upsertTemplate(current, saved));
      qc.setQueryData<WorkbenchTemplate[]>(keyFor(saved.surface), (current) =>
        upsertTemplate(current, saved),
      );
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: keyFor(saved.surface) });
    },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; req: UpdateTemplateRequest }) =>
      workbenchTemplatesApi.update(vars.id, vars.req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workbenchTemplatesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
